import { queryOptions } from '@tanstack/react-query';
import type {
  OrderDetailsResponse,
  OrderConfirmedResponse,
} from '@marxbiotech/shared-types/bff';

import { api } from '@lib/api/client';
import type { EnhancedStoreOrder } from '@modules/common/components/order-status-label';

// =============================================================================
// Types
// =============================================================================

interface ListOrdersParams {
  limit?: number;
  offset?: number;
  status?: string[];
}

interface ListOrdersResponse {
  orders: EnhancedStoreOrder[];
  count: number;
  offset: number;
  limit: number;
}

interface GetOrderResponse {
  order: EnhancedStoreOrder;
}

// =============================================================================
// Query Functions
// =============================================================================

const fetchOrders = async (
  params: ListOrdersParams = {}
): Promise<ListOrdersResponse> => {
  const { limit = 100, offset = 0, status } = params;
  const queryParams = new URLSearchParams();
  queryParams.set('limit', limit.toString());
  queryParams.set('offset', offset.toString());
  if (status) {
    status.forEach((s) => queryParams.append('status', s));
  }
  const response = await api.get<ListOrdersResponse>(
    `/orders?${queryParams.toString()}`
  );
  return response.data;
};

const fetchOrder = async (orderId: string): Promise<EnhancedStoreOrder> => {
  const response = await api.get<GetOrderResponse>(`/orders/${orderId}`);
  return response.data.order;
};

const fetchOrderDetails = async (
  orderId: string
): Promise<OrderDetailsResponse> => {
  const response = await api.get<OrderDetailsResponse>(
    `/orders/${orderId}/details`
  );
  return response.data;
};

const fetchOrderConfirmed = async (
  orderId: string
): Promise<OrderConfirmedResponse> => {
  const response = await api.get<OrderConfirmedResponse>(
    `/orders/${orderId}/confirmed`
  );
  return response.data;
};

// =============================================================================
// Query Keys
// =============================================================================

export const ordersKeys = {
  all: ['orders'] as const,
  lists: () => [...ordersKeys.all, 'list'] as const,
  list: (params: ListOrdersParams) => [...ordersKeys.lists(), params] as const,
  details: () => [...ordersKeys.all, 'detail'] as const,
  detail: (id: string) => [...ordersKeys.details(), id] as const,
  detailAggregated: (id: string) => [...ordersKeys.detail(id), 'details'] as const,
  detailConfirmed: (id: string) => [...ordersKeys.detail(id), 'confirmed'] as const,
};

// =============================================================================
// Query Options
// =============================================================================

const ORDER_GC_TIME = 10 * 60 * 1000; // 10 minutes

export const ordersQueryOptions = (params: ListOrdersParams = {}) =>
  queryOptions({
    queryKey: ordersKeys.list(params),
    queryFn: () => fetchOrders(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: ORDER_GC_TIME,
  });

export const orderDetailQueryOptions = (orderId: string) =>
  queryOptions({
    queryKey: ordersKeys.detail(orderId),
    queryFn: () => fetchOrder(orderId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: ORDER_GC_TIME,
  });

/** 聚合 API：含 enriched items、marxPayment、is_per_order_settlement */
export const orderDetailsQueryOptions = (orderId: string) =>
  queryOptions({
    queryKey: ordersKeys.detailAggregated(orderId),
    queryFn: () => fetchOrderDetails(orderId),
    staleTime: 30_000, // 30 seconds - order state may change
    gcTime: ORDER_GC_TIME,
  });

/** 聚合 API：結帳完成頁用，含 enriched items、marxPayment */
export const orderConfirmedQueryOptions = (orderId: string) =>
  queryOptions({
    queryKey: ordersKeys.detailConfirmed(orderId),
    queryFn: () => fetchOrderConfirmed(orderId),
    staleTime: 5 * 60 * 1000, // 5 min - confirmed order data rarely changes
    gcTime: ORDER_GC_TIME,
  });
